import { Ticker } from "pixi.js";
import { Object3D, Texture, Sprite, Vector3, Mesh, TubeGeometry, MeshBasicMaterial, DoubleSide, CatmullRomCurve3, SpriteMaterial, Material, MathUtils } from "three";

interface ProjectileTrailOptions {
    parent: Object3D;
    tubeCount?: number;
    tubeDistance?: number;
    tubeRadius?: number;
    tubeMaxPoints?: number;
    cloudTexture: Texture;
    cloudDistanceStep?: number;
    cloudFadeDuration?: number;
    cloudMaxScale?: number;
    lookAheadDistance?: number;
}

interface CloudParticle {
    sprite: Sprite;
    age: number;
}

export default class AirTrail {
    private target: Object3D;
    private parent: Object3D;

    // Tube properties
    private tubeCount: number;
    private tubeDistance: number;
    private tubeRadius: number;
    private tubeMaxPoints: number;
    private lookAheadDistance: number;

    private tubePoints: Vector3[][] = [];
    private trailMeshes: Mesh<TubeGeometry, MeshBasicMaterial>[] = [];
    private tubeMaterial: MeshBasicMaterial;

    // Cloud properties
    private cloudTexture: Texture;
    private cloudDistanceStep: number;
    private cloudFadeDuration: number;
    private cloudMaxScale: number;
    private lastPosition: Vector3 | null = null;
    private distanceAccumulator = 0;
    private particles: CloudParticle[] = [];

    constructor(target: Object3D, options: ProjectileTrailOptions) {
        this.target = target;
        this.parent = options.parent;

        this.tubeCount = options.tubeCount ?? 4;
        this.tubeDistance = options.tubeDistance ?? 0.1;
        this.tubeRadius = options.tubeRadius ?? 0.03;
        this.tubeMaxPoints = options.tubeMaxPoints ?? 15;
        this.lookAheadDistance = options.lookAheadDistance ?? 0.2;

        this.tubeMaterial = new MeshBasicMaterial({
            color: 0x474a4c,
            transparent: true,
            opacity: 0.7,
            side: DoubleSide
        });

        this.cloudTexture = options.cloudTexture;
        this.cloudDistanceStep = options.cloudDistanceStep ?? 0.5;
        this.cloudFadeDuration = options.cloudFadeDuration ?? 0.5;
        this.cloudMaxScale = options.cloudMaxScale ?? 2;

        for (let i = 0; i < this.tubeCount; i++) {
            this.trailMeshes.push(null as any);
            this.tubePoints.push([]);
        }

        Ticker.shared.add(this.update, this);
    }

    public update(_dt: number): void {
        const pos = this.target.getWorldPosition(new Vector3());

        const angles = [Math.PI * 5 / 3, Math.PI * 0, Math.PI / 3, Math.PI * 2 / 3];

        // Добавляем новые точки для каждой трубки
        for (let i = 0; i < this.tubeCount; i++) {
            const angle = angles[i];
            const radialOffset = new Vector3(Math.cos(angle), Math.sin(angle), 0)
                .multiplyScalar(this.tubeDistance);

            const tubePos = pos.clone().add(radialOffset);
            this.tubePoints[i].push(tubePos);

            if (this.tubePoints[i].length > this.tubeMaxPoints) {
                this.tubePoints[i].shift();
            }
        }

        this.updateTubes();

        // --- clouds ---
        if (this.lastPosition) {
            const distance = pos.distanceTo(this.lastPosition);
            this.distanceAccumulator += distance;

            if (this.distanceAccumulator >= this.cloudDistanceStep) {
                this.spawnCloud(pos);
                this.distanceAccumulator = 0;
            }
        }
        this.lastPosition = pos.clone();

        this.updateClouds(Ticker.shared.elapsedMS / 1000);
    }

    private updateTubes() {
        for (let i = 0; i < this.tubeCount; i++) {
            const points = this.tubePoints[i];
            if (points.length < 2) continue;

            // старый меш удаляем
            if (this.trailMeshes[i]) {
                this.parent.remove(this.trailMeshes[i]);
                this.trailMeshes[i].geometry.dispose();
            }

            // строим новую геометрию по своим точкам трубки
            const curve = new CatmullRomCurve3(points);
            const geometry = new TubeGeometry(curve, 20, this.tubeRadius, 8, false);
            const mesh = new Mesh(geometry, this.tubeMaterial);
            this.parent.add(mesh);
            this.trailMeshes[i] = mesh;
        }
    }

    private spawnCloud(position: Vector3) {
        const material = new SpriteMaterial({
            map: this.cloudTexture,
            transparent: true,
            opacity: 0,
            depthWrite: false
        });

        const sprite = new Sprite(material);
        sprite.position.copy(position);
        sprite.scale.setScalar(0);

        // Random rotation between -30° (11 o'clock) and +30° (1 o'clock)
        const minAngle = -Math.PI / 6; // -30°
        const maxAngle = Math.PI / 6; // +30°
        material.rotation = MathUtils.randFloat(minAngle, maxAngle);

        this.parent.add(sprite);
        this.particles.push({ sprite, age: 0 });
    }

    private updateClouds(deltaTime: number) {
        const toRemove: CloudParticle[] = [];

        for (const particle of this.particles) {
            particle.age += deltaTime * 0.5;

            // Normalized progress from 0 to 1
            const t = particle.age / this.cloudFadeDuration;

            // Update opacity from 1 -> 0
            particle.sprite.material.opacity = MathUtils.clamp(1 - t, 0, 1);

            // Update scale from 0.5 -> 2
            const scale = 0.5 + t * (2 - 0.5);
            particle.sprite.scale.setScalar(scale);

            if (t >= 1) {
                toRemove.push(particle);
                this.parent.remove(particle.sprite);
                (particle.sprite.material as Material).dispose();
            }
        }

        this.particles = this.particles.filter(p => !toRemove.includes(p));
    }

    public dispose(): void {
        Ticker.shared.remove(this.update, this);
        for (const mesh of this.trailMeshes) {
            if (mesh) {
                this.parent.remove(mesh);
                mesh.geometry.dispose();
            }
        }
        this.trailMeshes = [];

        for (const particle of this.particles) {
            this.parent.remove(particle.sprite);
            (particle.sprite.material as Material).dispose();
        }
        this.particles = [];
        this.tubePoints = [];
    }
}
